import React from "react";
import { BeatLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useFetchLocations } from "../../../hooks/useFetchLocations";
import { usePageSettings } from "../../../contexts/PageSettingsContext";
import LocationListBox from "../inputs/LocationListbox";
import InputWithOptions from "../inputs/InputWithOptions";
import { useSelectedPlanContext } from "../../../contexts/SelectedPlanContext";
import PrimaryButton from "../inputs/buttons/Button";
import useFetchResources from "../../../hooks/useFetchResources";
import { useQuery } from "@tanstack/react-query";
import { FaCheckCircle } from "react-icons/fa";
import InputTableTitle from "./InputTableTitle";
import ErrorMessage from "../ErrorMessage";
import InputTableContactUs from "./InputTableContactUs";
import { constructUrl } from "../../../utils/constructUrl";
import { navigate } from "gatsby";
import { getTranslatedCityName } from "../../../utils/getTranslatedCityName";

//Input table for Storage and IP Transit

const SecondaryInputTable = ({ serviceType, featureList, translations, defaultIataCode, pageContext }) => {
	const { selectedLocation, pageSettingsContext } = usePageSettings();
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();
	const { currency } = usePageSettings();

	const { city, language, country, hasCityInUrl, isDf, isHomepage } = pageSettingsContext;

	let citySlug = hasCityInUrl ? getTranslatedCityName(language, city) : null;
	let countrySlug = isDf ? "df" : isHomepage ? null : country;

	const handleOrderNow = (event) => {
		event.preventDefault();
		// Construct the complete target URL using pageContext and constructUrl method
		const translatedCheckoutUrl = constructUrl(language, countrySlug, citySlug, "/checkout");
		//Change the selectedPlan.service so the checkout can rander the corresponding form for the tertiary service
		const updatedSelectedPlan = {
			...selectedPlan,
			service: serviceType,
		};
		changeSelectedPlan(updatedSelectedPlan);
		// sessionStorage.removeItem("checkout-tab");
		navigate(translatedCheckoutUrl);
	};

	const queryLocations = useFetchLocations(
		serviceType,
		selectedLocation.iata_code !== "" ? selectedLocation.iata_code : defaultIataCode
	);

	const queryResources = useFetchResources(
		serviceType,
		selectedLocation.iata_code !== "" ? selectedLocation.iata_code : defaultIataCode
	);

	const fetchPrice = async () => {
		switch (serviceType) {
			case "storage":
				const storageData = {
					resources: [
						{
							location_code: selectedLocation.iata_code,
							service: serviceType,
							id_nas: selectedPlan?.nas?.id,
						},
					],
				};
				try {
					const response = await fetch(
						`${process.env.GATSBY_ADDON_PRICES_ENDPOINT}` + "?currency=" + currency + "&cycle=monthly",
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify(storageData),
						}
					);
					if (response.ok) {
						const data = await response.json();
						return data;
					} else {
						console.log(response.error);
						return [];
					}
				} catch (error) {
					console.error("Error:", error);
					return [];
				}
			case "iptransit":
				const ipTransitData = {
					resources: [
						{
							location_code: selectedLocation.iata_code,
							service: serviceType,
							id_bandwidth: selectedPlan?.bandwidth?.id,
						},
					],
				};
				try {
					const response = await fetch(
						`${process.env.GATSBY_ADDON_PRICES_ENDPOINT}` + "?currency=" + currency + "&cycle=monthly",
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify(ipTransitData),
						}
					);
					if (response.ok) {
						const data = await response.json();
						return data;
					} else {
						console.log(response.error);
						return [];
					}
				} catch (error) {
					console.error("Error:", error);
					return [];
				}
			default:
				console.error("Error. No could not fetch price for " + serviceType);
				return [];
		}
	};

	const queryPrice = useQuery({
		queryKey: ["price", currency, selectedLocation.iata_code, selectedPlan?.bandwidth?.id, selectedPlan?.nas?.id],
		queryFn: fetchPrice,
		refetchOnWindowFocus: false,
		enabled:
			queryLocations.isSuccess &&
			queryResources.isSuccess &&
			(selectedPlan?.bandwidth?.id !== undefined || selectedPlan?.nas?.id !== undefined),
	});

	const motionContainer = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	};

	const motionItem = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 },
	};

	const isLoadingPrice = queryLocations.isFetching || queryResources.isFetching || queryPrice.isFetching;

	return (
		<section className="container text-ipxon-black mx-auto flex flex-col gap-6 pb-20 w-full md:px-28 xl:px-0 px-4">
			<InputTableTitle
				serviceType={serviceType}
				selectedLocation={selectedLocation}
				translations={translations}
			/>

			{queryResources.isError ? (
				<div className="flex justify-center bg-gray-50 min-h-[16.875rem] py-8 px-6 rounded-lg w-full xl:w-3/4">
					<ErrorMessage translations={translations} />
				</div>
			) : (
				<div
					className={`flex flex-col xl:flex-row bg-gray-50 min-h-[16.875rem] py-8 px-6 gap-10 rounded-lg justify-between w-full xl:w-3/4`}
				>
					<div className={`flex flex-col justify-between gap-8 xl:w-1/3`}>
						<div className="flex flex-col justify-center items-center uppercase gap-4 w-full">
							<h4 className="flex self-start uppercase font-bold text-xs">
								{translations["LabelForLocationListBox"]}
							</h4>
							<LocationListBox
								color="white"
								locationList={queryLocations.isFetching ? [] : queryLocations.data}
								translations={translations}
							/>
						</div>
						{featureList && (
							<div className="w-full flex flex-col gap-4">
								<h4 className="flex uppercase font-bold text-xs">{translations["Features"]}</h4>
								<ul className="flex flex-col h-full gap-2">
									{featureList?.map((feature) => (
										<li key={feature} className="flex gap-2 items-center">
											<FaCheckCircle className="h-3 w-3 text-[#B9BFC1]" />
											<p className="text-sm">{translations[feature]}</p>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>

					<div className="xl:w-[10%] xl:min-h-[150px] flex flex-col xl:flex-row gap-4 xl:gap-3 2xl:gap-4 justify-around">
						{queryResources.isFetching ? (
							<span className="flex self-center justify-center w-full h-full items-center">
								<BeatLoader color="#d1d5db" size={16} />
							</span>
						) : (
							queryResources.isSuccess && (
								<>
									{serviceType === "storage" && queryResources?.data?.nas && (
										<InputWithOptions
											serviceType={serviceType}
											input={"NAS"}
											resources={queryResources?.data?.nas ? queryResources.data.nas : []}
											translations={translations}
											vertical={true}
										/>
									)}
									{serviceType === "iptransit" && queryResources?.data?.bandwidths && (
										<InputWithOptions
											serviceType={serviceType}
											input={"BANDWIDTH"}
											resources={
												queryResources?.data?.bandwidths ? queryResources.data.bandwidths : []
											}
											translations={translations}
											vertical={true}
										/>
									)}
								</>
							)
						)}
					</div>

					<div
						className={`xl:min-w-[300px] xl:w-1/3 border-t xl:border-t-0 pt-8 md:py-8 xl:py-0 xl:border-l border-gray-300 flex flex-col justify-center items-center font-bold uppercase gap-4`}
					>
						<div className="flex flex-col justify-center items-center text-3xl md:text-2xl 2xl:text-3xl gap-3">
							<motion.p
								initial="hidden"
								animate="visible"
								exit="hidden"
								variants={motionContainer}
								className="flex gap-1 h-7 items-center justify-center"
							>
								{isLoadingPrice ? (
									<BeatLoader color="#d1d5db" />
								) : (
									<motion.span variants={motionItem}>
										{queryPrice.data && typeof queryPrice.data.price !== "undefined"
											? queryPrice.data.price
											: "-"}
									</motion.span>
								)}
							</motion.p>
							<p className="text-gray-400 font-light text-center text-sm" key="payment">
								{translations["InputTableTimeSpan"]}
							</p>
						</div>
						<PrimaryButton
							onClick={handleOrderNow}
							className={`button-magenta flex reflect-below min-w-[175px] gap-1 h-8`}
						>
							{translations["InputTableButtonText"]}
						</PrimaryButton>
					</div>
				</div>
			)}

			<InputTableContactUs
				text={translations["InputTableDisclaimer"]}
				linkText={translations["InputTableDisclaimerLink"]}
				serviceType={serviceType}
				pageContext={pageContext}
			/>
		</section>
	);
};

export default SecondaryInputTable;
